// ============================================================================
// CLEO'S SECRET — Service card
// File: src/components/program/ServiceCard.jsx
//
// Renders one plan_services row joined with the services catalogue. Used
// in ProgramDashboard's Zone 1 "this week's services" list.
//
// State pill palette reuses billing's vocabulary so admin and patient
// surfaces speak the same colour language:
//   green:  delivered, scheduled
//   amber:  pending_schedule, in_progress
//   red:    cancelled, no_show, refunded, removed
//
// Action affordances ("Join" / "Reschedule") are V1 stubs — disabled
// chevron buttons with tooltip "Booking arrives Session 3".
// ============================================================================

import React from 'react';
import { P, FF, FS } from '../../lib/design-tokens';

function formatDateTime(iso) {
  if (!iso) return null;
  var d = new Date(iso);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ', ' +
    d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function statePillStyle(state) {
  if (state === 'delivered' || state === 'scheduled') {
    return { bg: P.GREEN_BG, fg: P.GREEN, border: P.GREEN };
  }
  if (state === 'pending_schedule' || state === 'in_progress') {
    return { bg: P.AMBER_BG, fg: P.AMBER, border: P.AMBER };
  }
  if (state === 'cancelled' || state === 'no_show' || state === 'refunded' || state === 'removed') {
    return { bg: P.RED_BG, fg: P.RED, border: P.RED };
  }
  return { bg: 'rgba(232,221,196,0.55)', fg: P.TF, border: P.SAND3 };
}

function stateLabel(state) {
  if (state === 'pending_schedule') return 'Not yet scheduled';
  if (state === 'in_progress') return 'In progress';
  if (state === 'no_show') return 'No show';
  if (!state) return 'Unknown';
  return state.charAt(0).toUpperCase() + state.slice(1);
}

function categoryLabel(category) {
  if (!category) return null;
  return category.split('_').map(function(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }).join(' ');
}

export default function ServiceCard(props) {
  var service = props.service;
  if (!service) return null;

  var pill = statePillStyle(service.state);
  var dateText = formatDateTime(service.scheduledAt) || 'Not yet scheduled';
  var actionLabel = service.state === 'scheduled' ? 'Join →' : 'Reschedule →';

  return (
    <div style={{
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      padding: 'clamp(16px, 1.8vw, 24px)',
      marginBottom: 10,
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: 'clamp(14px, 1.6vw, 20px)',
      alignItems: 'center',
    }}>
      <div style={{ minWidth: 0 }}>
        {/* Category pill */}
        {service.category && (
          <div style={{
            display: 'inline-block',
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 11px)',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            padding: '4px 9px',
            background: 'rgba(232,221,196,0.55)',
            color: P.BR2,
            border: '1px solid ' + P.SAND3,
            marginBottom: 8,
          }}>{categoryLabel(service.category)}</div>
        )}

        {/* Service name */}
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(17px, 1.45vw, 19px)',
          fontWeight: 600,
          color: P.INK,
          letterSpacing: '-0.008em',
          marginBottom: 4,
        }}>{service.serviceName || 'Service'}</div>

        {/* Date + duration */}
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(14px, 1.2vw, 16px)',
          fontStyle: 'italic',
          color: P.BR2,
          lineHeight: 1.5,
        }}>
          {dateText}
          {service.durationMinutes ? ' · ' + service.durationMinutes + ' min' : ''}
        </div>
      </div>

      {/* Right column: state pill + action stub */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        alignItems: 'flex-end',
      }}>
        <span style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          padding: '6px 11px',
          background: pill.bg,
          color: pill.fg,
          border: '1px solid ' + pill.border,
          whiteSpace: 'nowrap',
        }}>{stateLabel(service.state)}</span>

        <button
          disabled
          title="Booking arrives Session 3"
          style={{
            fontFamily: FS,
            fontSize: 'clamp(11px, 0.95vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            padding: '8px 14px',
            background: 'transparent',
            color: P.TF,
            border: '1px solid ' + P.SAND3,
            cursor: 'not-allowed',
            opacity: 0.6,
          }}>{actionLabel}</button>
      </div>
    </div>
  );
}
